// EQ Eight, drawn: the curve the browser plays (eq-response.ts) over a log
// frequency axis, one dot per band. Drag a dot sideways for frequency, up
// and down for gain (a cut or a notch only moves sideways); the wheel turns
// its Q. Below: the eight band switches, and the selected band's type,
// frequency, gain and Q as knobs — every move heard at once, proposed to
// the Set on release.

import { useMemo, useRef, useState, type PointerEvent, type WheelEvent } from "react";
import {
  clamp,
  EQ_BAND_LABELS,
  EQ_BAND_TYPES,
  EQ_FREQ_MAX,
  EQ_FREQ_MIN,
  EQ_Q_MAX,
  EQ_Q_MIN,
  eqEightBands,
  eqHasGain,
  findParam,
  readDb,
  writeDb,
  writeEqType,
  writeHz,
  writeQ,
  type EqBand,
} from "@web-shared/lib/device-params";
import { bandFilters, responseDb } from "@web-shared/lib/eq-response";
import { cn } from "@web-shared/lib/utils";
import { fmtDb, fmtHz, Knob, StepSelect } from "@web-shared/components/devices/controls";
import type { EditorProps } from "@web-shared/components/devices/types";

const W = 320;
const H = 120;
const DB_RANGE = 15;
export const BAND_COLORS = ["#e2c044", "#f0a45b", "#e96d6d", "#c86bd8", "#6b8fe2", "#5bc8d8", "#6bd88f", "#b6d86b"];

const xOf = (hz: number) => (Math.log(hz / EQ_FREQ_MIN) / Math.log(EQ_FREQ_MAX / EQ_FREQ_MIN)) * W;
const hzOf = (x: number) => EQ_FREQ_MIN * Math.pow(EQ_FREQ_MAX / EQ_FREQ_MIN, clamp(x / W, 0, 1));
const yOf = (db: number) => H / 2 - (clamp(db, -DB_RANGE * 1.4, DB_RANGE * 1.4) / DB_RANGE) * (H / 2 - 8);
const dbOf = (y: number) => clamp(((H / 2 - y) / (H / 2 - 8)) * DB_RANGE, -DB_RANGE, DB_RANGE);

/** Where a band's dot sits: on its gain for shelves and bells, on the curve's zero line for cuts. */
function dotY(b: EqBand): number {
  return yOf(eqHasGain(b.type) ? b.gain : 0);
}

export function EqEightEditor({ device, disabled, set }: EditorProps) {
  const bands = useMemo(() => eqEightBands(device), [device]);
  const [selected, setSelected] = useState<number>(() => bands.find((b) => b.on)?.n ?? bands[0]?.n ?? 1);
  const svg = useRef<SVGSVGElement | null>(null);
  const drag = useRef<{ band: EqBand; moved: boolean } | null>(null);
  const output = findParam(device, "Output Gain");

  const curve = useMemo(() => {
    const filters = bands.flatMap((b) => bandFilters(b));
    const pts: string[] = [];
    for (let i = 0; i <= 160; i++) {
      const x = (i / 160) * W;
      pts.push(`${x.toFixed(1)},${yOf(responseDb(filters, hzOf(x))).toFixed(1)}`);
    }
    return pts.join(" ");
  }, [bands]);

  const band = bands.find((b) => b.n === selected) ?? bands[0];

  /** Move one of a band's parameters (display units → the raw value the Set reads). */
  const setBand = (b: EqBand, what: "freq" | "gain" | "q", v: number, commit: boolean) => {
    const i = b.idx[what];
    if (i === undefined) return;
    const p = device.params[i]!;
    const raw = what === "freq" ? writeHz(p, v, EQ_FREQ_MIN, EQ_FREQ_MAX) : what === "gain" ? writeDb(p, v) : writeQ(p, v);
    set(i, raw, commit);
  };

  const local = (e: PointerEvent<SVGElement>): { x: number; y: number } => {
    const r = svg.current!.getBoundingClientRect();
    return { x: ((e.clientX - r.left) / r.width) * W, y: ((e.clientY - r.top) / r.height) * H };
  };
  const onDotDown = (e: PointerEvent<SVGCircleElement>, b: EqBand) => {
    e.stopPropagation();
    setSelected(b.n);
    if (disabled) return;
    (e.target as Element).setPointerCapture(e.pointerId);
    drag.current = { band: b, moved: false };
  };
  const onMove = (e: PointerEvent<SVGElement>, commit: boolean) => {
    const d = drag.current;
    if (!d) return;
    const { x, y } = local(e);
    if (!commit) d.moved = true;
    if (!d.moved) return;
    setBand(d.band, "freq", hzOf(x), commit);
    if (eqHasGain(d.band.type)) setBand(d.band, "gain", dbOf(y), commit);
  };
  const onUp = (e: PointerEvent<SVGElement>) => {
    onMove(e, true);
    drag.current = null;
  };
  const onWheel = (e: WheelEvent<SVGCircleElement>, b: EqBand) => {
    if (disabled || b.idx.q === undefined) return;
    e.stopPropagation();
    const next = clamp(b.q * Math.pow(1.1, e.deltaY < 0 ? 1 : -1), EQ_Q_MIN, EQ_Q_MAX);
    setBand(b, "q", next, true);
  };

  const grid = [100, 1000, 10000];
  return (
    <div className="flex flex-col gap-2" data-testid="eq-eight">
      <svg
        ref={svg}
        viewBox={`0 0 ${W} ${H}`}
        className="h-[120px] w-[320px] touch-none rounded bg-[var(--bg-deep)]"
        onPointerMove={(e) => onMove(e, false)}
        onPointerUp={onUp}
        onPointerCancel={onUp}
        role="img"
        aria-label="EQ Eight frequency response"
      >
        {[20, 50, 200, 500, 2000, 5000, 20000].map((f) => (
          <line key={f} x1={xOf(f)} x2={xOf(f)} y1={0} y2={H} stroke="var(--hairline)" />
        ))}
        {grid.map((f) => (
          <g key={f}>
            <line x1={xOf(f)} x2={xOf(f)} y1={0} y2={H} stroke="var(--border-panel)" />
            <text x={xOf(f) + 2} y={H - 3} fontSize={8} fill="var(--muted-foreground)">
              {fmtHz(f)}
            </text>
          </g>
        ))}
        {[-12, -6, 6, 12].map((db) => (
          <line key={db} x1={0} x2={W} y1={yOf(db)} y2={yOf(db)} stroke="var(--hairline)" />
        ))}
        <line x1={0} x2={W} y1={yOf(0)} y2={yOf(0)} stroke="var(--border-panel)" />
        <polyline points={`0,${H} ${curve} ${W},${H}`} fill="var(--primary)" opacity={0.08} />
        <polyline points={curve} fill="none" stroke="var(--primary)" strokeWidth={1.5} />
        {bands.map((b) => (
          <g key={b.n}>
            <circle
              cx={xOf(b.freq)}
              cy={dotY(b)}
              r={b.n === selected ? 7 : 6}
              fill={b.on ? BAND_COLORS[b.n - 1] : "var(--control)"}
              stroke={b.n === selected ? "var(--foreground)" : BAND_COLORS[b.n - 1]}
              strokeWidth={b.n === selected ? 1.5 : 1}
              className={cn(!disabled && "cursor-move")}
              onPointerDown={(e) => onDotDown(e, b)}
              onWheel={(e) => onWheel(e, b)}
              data-testid={`eq-band-${b.n}`}
            />
            <text x={xOf(b.freq)} y={dotY(b) + 3} fontSize={8} textAnchor="middle" fill={b.on ? "#000" : "var(--muted-foreground)"} pointerEvents="none">
              {b.n}
            </text>
          </g>
        ))}
      </svg>
      <div className="flex items-center gap-1">
        {bands.map((b) => (
          <button
            key={b.n}
            type="button"
            onClick={() => setSelected(b.n)}
            onDoubleClick={() => b.idx.on !== undefined && !disabled && set(b.idx.on, b.on ? device.params[b.idx.on]!.min : device.params[b.idx.on]!.max, true)}
            title={`Band ${b.n}: ${EQ_BAND_LABELS[b.type]}${b.on ? "" : " (off)"} — double-click to switch it ${b.on ? "off" : "on"}`}
            className={cn("flex h-5 w-6 items-center justify-center rounded text-[9px] font-semibold", b.n === selected ? "ring-1 ring-foreground" : "")}
            style={{ background: b.on ? BAND_COLORS[b.n - 1] : "var(--control)", color: b.on ? "#000" : "var(--muted-foreground)" }}
            aria-label={`Band ${b.n}`}
            aria-pressed={b.n === selected}
          >
            {b.n}
          </button>
        ))}
      </div>
      {band && (
        <div className="flex items-end gap-1">
          {band.idx.on !== undefined && (
            <button
              type="button"
              disabled={disabled}
              onClick={() => set(band.idx.on!, band.on ? device.params[band.idx.on!]!.min : device.params[band.idx.on!]!.max, true)}
              aria-pressed={band.on}
              className={cn("mb-4 h-5 rounded px-1.5 text-[9px] font-semibold", band.on ? "text-black" : "bg-[var(--control)] text-muted-foreground")}
              style={band.on ? { background: BAND_COLORS[band.n - 1] } : undefined}
              title={`Switch band ${band.n} ${band.on ? "off" : "on"}`}
            >
              {band.on ? "ON" : "OFF"}
            </button>
          )}
          {band.idx.type !== undefined && (
            <div className="mb-3 w-[84px]">
              <StepSelect
                label="Type"
                value={EQ_BAND_TYPES.indexOf(band.type)}
                options={EQ_BAND_TYPES.map((t, i) => ({ value: i, label: EQ_BAND_LABELS[t] }))}
                disabled={disabled}
                onChange={(i) => set(band.idx.type!, writeEqType(device.params[band.idx.type!]!, EQ_BAND_TYPES[i]!), true)}
              />
            </div>
          )}
          {band.idx.freq !== undefined && (
            <Knob label="Freq" value={band.freq} min={EQ_FREQ_MIN} max={EQ_FREQ_MAX} log format={fmtHz} disabled={disabled} accent={BAND_COLORS[band.n - 1]} onDraft={(v) => setBand(band, "freq", v, false)} onCommit={(v) => setBand(band, "freq", v, true)} />
          )}
          {band.idx.gain !== undefined && (
            <Knob label="Gain" value={band.gain} min={-15} max={15} bipolar format={fmtDb} disabled={disabled || !eqHasGain(band.type)} accent={BAND_COLORS[band.n - 1]} onDraft={(v) => setBand(band, "gain", v, false)} onCommit={(v) => setBand(band, "gain", v, true)} />
          )}
          {band.idx.q !== undefined && (
            <Knob label="Q" value={band.q} min={EQ_Q_MIN} max={EQ_Q_MAX} log format={(v) => v.toFixed(2)} disabled={disabled} accent={BAND_COLORS[band.n - 1]} onDraft={(v) => setBand(band, "q", v, false)} onCommit={(v) => setBand(band, "q", v, true)} />
          )}
          {output && (
            <Knob label="Output" value={readDb(output.p, 0)} min={output.p.min < 0 ? output.p.min : -12} max={output.p.min < 0 ? output.p.max : 12} bipolar format={fmtDb} disabled={disabled} onDraft={(v) => set(output.index, writeDb(output.p, v), false)} onCommit={(v) => set(output.index, writeDb(output.p, v), true)} />
          )}
        </div>
      )}
      {bands.length === 0 && <div className="text-[10px] text-muted-foreground">The Set reported no bands for this EQ.</div>}
    </div>
  );
}
